import { isNil } from "lodash";
import { mapGetters } from "vuex";
import { BusinessPlanStatus, TabEnum } from "../../businessplan.types";
import { adjustmentAccountingStep, adjustmentStep } from "@/templates/ativacaoped/config/workflow.config";
import { AnyAdmin } from "@/core/enums/user-types";

/**
 * @mixin
 * @description Fornece as funcionalidades básicas do formulário, como gerenciamento de abas
 * @example
 * mixins: [BusinessPlanCore]
 */
export default {
  data() {
    const self = this;

    return {
      /** @type {import('../../businessplan.types').BusinessPlanFormData} */
      formData: {},
      /** Determina qual aba esta aberta
       * @type {number}
       */
      currentStep: 0,
      /** @type {import('../../businessplan.types').TabItem[]} */
      tabs: [
        {
          id: TabEnum.BP,
          label: "Business plan",
          errors: 0
        },
        {
          id: TabEnum.BENEFICIO_ECONOMICO,
          label: "Benefício econômico futuro",
          errors: 0
        },
        {
          id: TabEnum.ROADMAP,
          label: "Roadmap",
          errors: 0,
          get disabled() {
            return !self.businessPlanId;
          },
          get tooltip() {
            return !self.businessPlanId ? "Salve para poder editar esta aba" : "";
          }
        },
        {
          id: TabEnum.ALOCACAO,
          label: "Participantes e Alocações",
          errors: 0,
          get disabled() {
            return (
              !self.businessPlanId ||
              !self.formData.data_inicio ||
              !self.formData.data_fim
            );
          },
          get tooltip() {
            if (!self.businessPlanId) {
              return "Salve para poder editar esta aba";
            }

            if (!self.formData.data_inicio || !self.formData.data_fim) {
              return "Cadastre um roadmap para poder editar esta aba";
            }

            return "";
          }
        },
        {
          id: TabEnum.OUTRAS_DESPESAS,
          label: "Terceiros e Outras despesas",
          errors: 0,
          get disabled() {
            return (
              !self.businessPlanId ||
              !self.formData.data_inicio ||
              !self.formData.data_fim
            );
          },
          get tooltip() {
            if (!self.businessPlanId) {
              return "Salve para poder editar esta aba";
            }

            if (!self.formData.data_inicio || !self.formData.data_fim) {
              return "Cadastre um roadmap para poder editar esta aba";
            }

            return "";
          }
        },
        {
          id: TabEnum.VPL,
          label: "VPL",
          errors: 0,
          get disabled() {
            return (
              (!self.businessPlanId ||
                !self.formData.data_inicio ||
                !self.formData.data_fim ||
                !self.formData.vida_util)
            );
          },
          get tooltip() {
            if (!self.businessPlanId) {
              return "Salve para poder editar esta aba";
            }

            if (self.formData) {
              const { data_inicio, data_fim, vida_util } = self.formData;
              const vplRequeriments = [];

              if (!data_inicio || !data_fim) {
                vplRequeriments.push("cadastre um roadmap");
              }

              if (!vida_util) {
                vplRequeriments.push(
                  "defina a vida útil na aba benefício econômico futuro"
                );
              }

              return !vplRequeriments.length
                ? ""
                : `Para calcular o VPL ${vplRequeriments.join(" e ")}`;
            }

            return "";
          }
        },
        {
          id: TabEnum.MEMORIA_EVIDENCIAS,
          label: "Memória de cálculo e Evidências",
          errors: 0,
          get disabled() {
            return !self.businessPlanId;
          },
          get tooltip() {
            return this.disabled ? "Salve para poder editar esta aba" : "";
          }
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    /**
     * Acesso direto ao id do business plan, se não for o fluxo de cadastro
     * @type {number | null}
     * @readonly
     */
    businessPlanId() {
      return this.$route.params.id || this.formData?.id || null;
    },
    /**
     * Acesso direto as propriedades disabled de cada aba
     * @type {{ [id: string]: boolean }}
     * @readonly
     */
    tabsDisabled() {
      return this.tabs.reduce(
        (acc, { id, disabled }) => ({ ...acc, [id]: disabled }),
        {}
      );
    },
    /**
     * Determina se os campos do formulário podem ser alterados
     * @type {boolean}
     * @readonly
     */
    editable() {
      // `editavel` diz se o perfil de usuário tem permissão para edição
      const { editavel, id, status, workflow_id } = this.formData;
      const isCreateMode = isNil(id);
      const inWorkflow = status === BusinessPlanStatus.APROVACAO;
      const inFilling = [BusinessPlanStatus.PREENCHIMENTO, BusinessPlanStatus.PREENCHIMENTO_NOVA_VERSAO].includes(status);
      const editableStep = [adjustmentStep.id, adjustmentAccountingStep.id].includes(workflow_id);

      return isCreateMode || editavel && ((inWorkflow && editableStep) || inFilling);
    },
    /**
     * Determina se o BP foi criado e está na etapa de preenchimento pelo demandante
     * @type {boolean}
     * @readonly
     */
    inFilling() {
      const { id, status } = this.formData || {};
      return !isNil(id) && BusinessPlanStatus.PREENCHIMENTO === status;
    },
    /**
     * Determina se o BP foi criado e está na etapa de preenchimento de versão pelo demandante
     * @type {boolean}
     * @readonly
     */
    inVersionFilling() {
      const { id, status } = this.formData || {};
      return (
        !isNil(id) && BusinessPlanStatus.PREENCHIMENTO_NOVA_VERSAO === status
      );
    },
    /**
     * Determina se o usuário logado é o demandante (i.e. criador) do BP.
     * @type {boolean}
     * @readonly
     */
    isDemandant() {
      const {
        lider_tecnologia,
        lider_produto,
        lider_negocio,
        usuario_id
      } = this.formData || {};

      const isOwner = usuario_id === this.user.id;

      const responsavelId = this.user.responsavel?.id;
      const isLeader = responsavelId && [lider_tecnologia, lider_produto, lider_negocio].includes(responsavelId);

      return isOwner || isLeader;
    },
    /**
     * Determina se o usuário logado é um usuário interno Galápagos de nível de
     * sistema (Administrador ou Consultor), independente do vínculo com o BP.
     * @type {boolean}
     * @readonly
     */
    isSystemAdmin() {
      return AnyAdmin.includes(this.user.tipo_usuario);
    },
    /**
     * Integração com API para salvar e recuperar os dados do Business Plan
     * @type {Object}
     * @readonly
     */
    resource() {
      return this.apiResource(
        `/v1/ativacaoped/${this.clientId}/business-plans`
      );
    },
    /**
     * Se a aba atual for um formulário, mostra botão para salvar alterações no formulário.
     * Demais abas tem salvamento interno.
     * @type {boolean}
     * @readonly
     */
    showSaveBtn() {
      return (
        this.editable &&
        [TabEnum.BENEFICIO_ECONOMICO, TabEnum.BP].includes(this.currentStep)
      );
    },
    TabEnum() {
      return TabEnum;
    }
  },
  methods: {
    /**
     * Carrega os dados do Business Plan, processa parte dos dados e os atribui a variavel formData
     * @returns {void}
     */
    async doLoad() {
      if (!this.businessPlanId) {
        return;
      }

      try {
        const response = await this.resource.get(this.businessPlanId);
        this.formData = response.data;

        const {
          ativo_controle,
          ativo_produto_servico_semelhante,
          engenharia_valor,
          mercado_comercializacao,
          participantes_registro_horas
        } = this.formData;

        this.formData.ativo_produto_servico_semelhante = !!ativo_produto_servico_semelhante;
        this.formData.mercado_comercializacao = !!mercado_comercializacao;
        this.formData.engenharia_valor = !!engenharia_valor;
        this.formData.participantes_registro_horas = !!participantes_registro_horas;
        this.formData.ativo_controle = !!ativo_controle;

        this.formData.status_workflow_label =
          response.data?.workflow?.status_label ?? null;
        this.formData.status_workflow = response.data?.workflow?.status ?? null;
        this.formData.diretoriaId =
          response.data?.business_units?.length > 0
            ? response.data.business_units[0].divisao_id
            : null;
        this.formData.lider_tecnologia = response.data?.lider_tecnologia?.id ?? null;
        this.formData.lider_produto = response.data?.lider_produto?.id ?? null;
        this.formData.lider_negocio = response.data?.lider_negocio?.id ?? null;
        this.formData.responsaveis_acompanhamento = response.data?.responsaveis_acompanhamento?.map(({ id }) => id) ?? [];
      } catch (error) {
        this.formData = {};
      }
    },
    /**
     * Cadastra os dados de um novo Business Plan ou atualiza de um pré-existente, desde que os campos estejam válidos.
     * Dados pegos da váriavel formData
     * @param {boolean} [skip=false] indica se deve ou não pular o reload da tela após uma atualização bem sucedida
     * @returns {void}
     * @requires validateForms desse mesmo mixin
     */
    async doSave(skip = false) {
      if (!this.validateForms()) {
        return;
      }

      if (!this.formData.usuario_id) {
        this.formData.usuario_id = this.user.id;
      }

      // Acessando o formulário da primeira aba para ter a mesma lista de emails carregada lá
      if (this.$refs.bpform) {
        const { opts: { emails } } = this.$refs.bpform;
        const { lider_tecnologia, lider_produto, lider_negocio, responsaveis_acompanhamento } = this.formData;

        if (!isNil(lider_tecnologia)) {
          this.formData.lider_tecnologia = emails.find(({ id }) => lider_tecnologia === id) ?? null;
        }

        if (!isNil(lider_produto)) {
          this.formData.lider_produto = emails.find(({ id }) => lider_produto === id) ?? null;
        }

        if (!isNil(lider_negocio)) {
          this.formData.lider_negocio = emails.find(({ id }) => lider_negocio === id) ?? null;
        }

        if (Array.isArray(responsaveis_acompanhamento) && responsaveis_acompanhamento.length > 0) {
          this.formData.responsaveis_acompanhamento = emails.filter(({ id }) => responsaveis_acompanhamento.includes(id));
        }
      }

      const response = await this.resource.save(
        this.formData,
        this.formData.id
      );

      this.$notify({
        group: "geral",
        duration: 5000,
        type: "sucess",
        title: `${this.businessPlanId ? "Edição" : "Inclusão"} concluída`,
        text: `Business Plan ${this.businessPlanId ? "alterado" : "criado"
          } com sucesso.`
      });

      if (skip) {
        return;
      }

      if (this.businessPlanId) {
        this.doLoad();
      } else {
        this.$router.push({
          name: "ativacaoped-projetos-form-edicao",
          params: { id: response.data.id }
        });
      }
    },
    /**
     * Método auxiliar para atualizar o contador de campos inválidos em cada aba do formulário
     * @param {string} key identificador da aba segundo enum TabEnum
     * @param {number} num quantidade de campos inválidos
     */
    onError(key, num) {
      const found = this.tabs.find(({ id }) => id === key);

      if (found) {
        found.errors = num;
      }
    },
    /**
     * Retorna a validade do formulário.
     * No modo 'save' (padrão) valida apenas o mínimo de campos necessários para diferenciar um Business Plan de outro.
     * No modo 'approve' valida todos os campos obrigatórios para enviar o Business Plan para o fluxo de aprovação (i.e. workflow).
     * Precisa que todas as abas tenham uma referencia DOM e que as abas obrigatórias tenham um método chamado 'validate'.
     * @param {'save' | 'approve'} mode modo de validação
     * @returns {boolean} se o formulário está válido ou não
     */
    validateForms(mode = "save") {
      const isAllValid = Object.values(this.$refs).reduce((valid, tab) => {
        if ("validate" in tab) {
          const validTab = tab.validate(mode);
          return valid && validTab;
        }

        return valid;
      }, true);

      return isAllValid;
    }
  }
};
